// 历史名人数据库
// 时间范围：公元前3000年（三皇五帝）到公元1911年（清朝灭亡）

import type { Talent } from './talentData'

export interface HistoricalTalent {
  id: string;
  name: string;
  surname: string;
  fullName: string;
  styleName?: string;
  birthYear: number;    // 出生年份（负数表示公元前）
  deathYear: number;    // 去世年份（负数表示公元前）
  school: 'confucian' | 'legalist' | 'mohist' | 'taoist' | 'military' | 'diplomat' | 'merchant' | 'farmer';
  era: string;          // 所属朝代/时期
  
  // 基础属性（历史人物有固定属性，不随机）
  politics: number;
  military: number;
  intelligence: number;
  charisma: number;
  
  // 人物生平故事
  biography: {
    summary: string;           // 一句话简介
    earlyLife: string;         // 早年经历
    majorEvents: string[];     // 主要事迹
    achievements: string[];    // 主要成就
    legacy: string;            // 历史评价/影响
    quotes?: string[];         // 名言警句
  };
  
  // 特殊标签
  tags: string[];
}

// 历史人物数据库
export const HISTORICAL_TALENTS: HistoricalTalent[] = [
  // ===== 先秦时期 =====
  {
    id: 'confucius',
    name: '丘',
    surname: '孔',
    fullName: '孔丘',
    styleName: '仲尼',
    birthYear: -551,
    deathYear: -479,
    school: 'confucian',
    era: '春秋',
    politics: 95,
    military: 40,
    intelligence: 90,
    charisma: 98,
    biography: {
      summary: '儒家学派创始人，被尊为"至圣先师"，其思想影响中国两千余年',
      earlyLife: '生于鲁国陬邑，三岁丧父，家道中落。少年时做过委吏、乘田等小官，后立志求学。',
      majorEvents: [
        '三十岁开始收徒讲学，开创私学',
        '五十岁出任鲁国司寇，摄行相事',
        '周游列国十四年，宣扬仁义之道',
        '晚年归鲁，删订六经，整理古籍'
      ],
      achievements: [
        '创立儒家学派，提出"仁"的核心思想',
        '主张"为政以德"，强调德治',
        '提倡"有教无类"，打破贵族教育垄断',
        '整理《诗》《书》《礼》《乐》《易》《春秋》'
      ],
      legacy: '孔子被后世尊为"万世师表"，其思想成为中国传统文化的主流，影响遍及东亚。',
      quotes: [
        '学而时习之，不亦说乎',
        '己所不欲，勿施于人',
        '三人行，必有我师焉',
        '知之为知之，不知为不知，是知也'
      ]
    },
    tags: ['圣人', '万世师表', '至圣先师']
  },
  {
    id: 'laozi',
    name: '耳',
    surname: '李',
    fullName: '李耳',
    styleName: '聃',
    birthYear: -571,
    deathYear: -471,
    school: 'taoist',
    era: '春秋',
    politics: 70,
    military: 30,
    intelligence: 98,
    charisma: 85,
    biography: {
      summary: '道家学派创始人，著有《道德经》，被尊为道教始祖',
      earlyLife: '曾任周朝守藏室之史，管理国家典籍，博闻强识。',
      majorEvents: [
        '长期担任周朝史官，博览群书',
        '见周室衰微，骑青牛西出函谷关',
        '应关令尹喜之请，著《道德经》五千言',
        '此后不知所踪，成千古之谜'
      ],
      achievements: [
        '创立道家学派，提出"道法自然"',
        '主张"无为而治"的政治理念',
        '著有《道德经》，为道家经典',
        '思想影响中国哲学、政治、文化'
      ],
      legacy: '老子被道教尊为太上老君，其思想与儒家互补，共同构成中国传统文化核心。',
      quotes: [
        '道可道，非常道；名可名，非常名',
        '上善若水，水善利万物而不争',
        '知人者智，自知者明',
        '千里之行，始于足下'
      ]
    },
    tags: ['道家始祖', '太上老君']
  },
  {
    id: 'sunzi',
    name: '武',
    surname: '孙',
    fullName: '孙武',
    styleName: '长卿',
    birthYear: -545,
    deathYear: -470,
    school: 'military',
    era: '春秋',
    politics: 60,
    military: 100,
    intelligence: 95,
    charisma: 75,
    biography: {
      summary: '兵家至圣，著有《孙子兵法》，被誉为"兵学圣典"',
      earlyLife: '齐国乐安人，出身军事世家，自幼研习兵法。',
      majorEvents: [
        '由齐入吴，隐居著书',
        '以兵法十三篇见吴王阖闾',
        '训练宫女，斩杀吴王宠姬立威',
        '辅佐吴王破楚入郢，威震诸侯'
      ],
      achievements: [
        '著《孙子兵法》十三篇',
        '提出"知己知彼，百战不殆"',
        '创立中国古代军事理论体系',
        '被尊为"兵圣"、"兵家至圣"'
      ],
      legacy: '《孙子兵法》是世界最早的军事著作，影响遍及军事、商业、体育等领域。',
      quotes: [
        '知己知彼，百战不殆',
        '上兵伐谋，其次伐交，其次伐兵，其下攻城',
        '兵者，诡道也',
        '不战而屈人之兵，善之善者也'
      ]
    },
    tags: ['兵圣', '兵家至圣']
  },
  {
    id: 'guanzhong',
    name: '夷吾',
    surname: '管',
    fullName: '管仲',
    styleName: '仲',
    birthYear: -723,
    deathYear: -645,
    school: 'legalist',
    era: '春秋',
    politics: 98,
    military: 75,
    intelligence: 92,
    charisma: 80,
    biography: {
      summary: '春秋第一相，辅佐齐桓公成为春秋首霸',
      earlyLife: '颍上人，早年与鲍叔牙经商，后辅佐公子纠。',
      majorEvents: [
        '公子纠败，经鲍叔牙推荐辅佐齐桓公',
        '推行改革，富国强兵',
        '尊王攘夷，九合诸侯',
        '一匡天下，成为春秋首霸'
      ],
      achievements: [
        '推行"相地而衰征"的税收政策',
        '实行军政合一、兵民合一',
        '发展盐铁官营，增强国力',
        '提出"仓廪实而知礼节"'
      ],
      legacy: '孔子赞曰："微管仲，吾其被发左衽矣。"被尊为"法家先驱"、"华夏第一相"。',
      quotes: [
        '仓廪实而知礼节，衣食足而知荣辱',
        '政之所兴，在顺民心'
      ]
    },
    tags: ['华夏第一相', '法家先驱']
  },
  {
    id: 'mozi',
    name: '翟',
    surname: '墨',
    fullName: '墨翟',
    styleName: '子',
    birthYear: -468,
    deathYear: -376,
    school: 'mohist',
    era: '战国',
    politics: 70,
    military: 65,
    intelligence: 90,
    charisma: 85,
    biography: {
      summary: '墨家学派创始人，主张"兼爱""非攻"',
      earlyLife: '宋国人，一说鲁国人，出身工匠，善造器械。',
      majorEvents: [
        '早年学儒，后创墨家',
        '率弟子周游列国，止楚攻宋',
        '与鲁班斗法，展示守城之术',
        '建立严密的墨家组织'
      ],
      achievements: [
        '创立墨家学派，与儒家并称"显学"',
        '提出"兼爱""非攻""尚贤"等主张',
        '在光学、力学、逻辑学方面有贡献',
        '擅长守城技术，著《城守》各篇'
      ],
      legacy: '墨家是先秦时期与儒家齐名的显学，其科学思想和逻辑学成就尤为珍贵。',
      quotes: [
        '兼相爱，交相利',
        '非攻止战，天下兼爱'
      ]
    },
    tags: ['墨家始祖']
  },
  {
    id: 'shangyang',
    name: '鞅',
    surname: '公孙',
    fullName: '公孙鞅',
    styleName: '鞅',
    birthYear: -395,
    deathYear: -338,
    school: 'legalist',
    era: '战国',
    politics: 90,
    military: 70,
    intelligence: 88,
    charisma: 60,
    biography: {
      summary: '法家代表人物，商鞅变法使秦国强大',
      earlyLife: '卫国人，又称卫鞅，少好刑名之学，事魏相公叔痤。',
      majorEvents: [
        '闻秦孝公求贤，西入秦国',
        '三次见孝公，终以霸道说之',
        '推行变法，立木为信',
        '率秦军收复河西之地'
      ],
      achievements: [
        '推行商鞅变法，废井田、开阡陌',
        '实行郡县制，加强中央集权',
        '奖励耕战，富国强兵',
        '统一度量衡，制定秦律'
      ],
      legacy: '商鞅变法为秦统一六国奠定基础，其法治思想影响深远，虽死而法不灭。',
      quotes: [
        '治世不一道，便国不法古'
      ]
    },
    tags: ['法家代表', '变法先驱']
  },
  {
    id: 'hanfeizi',
    name: '非',
    surname: '韩',
    fullName: '韩非',
    styleName: '子',
    birthYear: -280,
    deathYear: -233,
    school: 'legalist',
    era: '战国',
    politics: 85,
    military: 50,
    intelligence: 95,
    charisma: 40,
    biography: {
      summary: '法家集大成者，其思想为秦始皇所推崇',
      earlyLife: '韩国公子，与李斯同为荀子的学生，口吃而善著书。',
      majorEvents: [
        '多次上书韩王，主张变法图强',
        '著《孤愤》《五蠹》等传于秦',
        '秦王见书，叹曰："寡人得见此人与之游，死不恨矣！"',
        '入秦后被李斯陷害，死于狱中'
      ],
      achievements: [
        '集法家思想之大成',
        '提出"法、术、势"相结合的治国理论',
        '著有《韩非子》五十五篇',
        '思想成为秦朝治国理念'
      ],
      legacy: '韩非是法家思想的集大成者，其理论对中国古代政治制度影响深远。',
      quotes: [
        '法不阿贵，绳不挠曲',
        '世异则事异，事异则备变'
      ]
    },
    tags: ['法家集大成者']
  },
  {
    id: 'sushiqin',
    name: '秦',
    surname: '苏',
    fullName: '苏秦',
    styleName: '季子',
    birthYear: -337,
    deathYear: -284,
    school: 'diplomat',
    era: '战国',
    politics: 85,
    military: 40,
    intelligence: 92,
    charisma: 95,
    biography: {
      summary: '纵横家代表人物，合纵六国抗秦',
      earlyLife: '洛阳人，早年游说秦王不成，穷困而归，受家人讥笑。',
      majorEvents: [
        '发愤读书，锥刺股',
        '游说六国，合纵抗秦',
        '佩六国相印，为纵约长',
        '后入齐为客卿，被刺杀'
      ],
      achievements: [
        '成功合纵六国，使秦兵不敢出函谷关十五年',
        '佩六国相印，位极人臣',
        '纵横家的代表人物',
        '著《苏子》三十一篇'
      ],
      legacy: '苏秦是战国时期最著名的纵横家，其合纵策略影响了战国格局。',
      quotes: [
        '使我有洛阳二顷田，安能佩六国相印'
      ]
    },
    tags: ['纵横家', '六国相印']
  },
  {
    id: 'zhangyi',
    name: '仪',
    surname: '张',
    fullName: '张仪',
    styleName: '子',
    birthYear: -350,
    deathYear: -310,
    school: 'diplomat',
    era: '战国',
    politics: 88,
    military: 45,
    intelligence: 90,
    charisma: 88,
    biography: {
      summary: '纵横家代表人物，连横破合纵',
      earlyLife: '魏国人，与苏秦同师鬼谷子，首创连横之策。',
      majorEvents: [
        '入秦见惠文王，提出连横之策',
        '游说六国，破坏合纵',
        '相秦多年，多次破六国联盟',
        '晚年入魏为相，卒于魏'
      ],
      achievements: [
        '以连横破合纵，瓦解六国联盟',
        '多次出任秦相，助秦扩张',
        '纵横家的代表人物',
        '著《张子》十篇'
      ],
      legacy: '张仪与苏秦一横一纵，代表了战国时期外交策略的两个极端。',
      quotes: [
        '连横而斗诸侯'
      ]
    },
    tags: ['纵横家', '连横']
  },
  
  // ===== 秦汉时期 =====
  {
    id: 'xiaohe',
    name: '何',
    surname: '萧',
    fullName: '萧何',
    styleName: '',
    birthYear: -257,
    deathYear: -193,
    school: 'legalist',
    era: '西汉',
    politics: 95,
    military: 30,
    intelligence: 88,
    charisma: 75,
    biography: {
      summary: '汉初三杰之一，被誉为"开国第一功臣"',
      earlyLife: '沛县人，早年任秦沛县狱吏，与刘邦交好。',
      majorEvents: [
        '随刘邦起义，负责后勤',
        '入咸阳，收秦律令图书',
        '楚汉相争，镇守关中',
        '辅佐刘邦建立汉朝'
      ],
      achievements: [
        '制定《九章律》，完善汉律',
        '推行与民休息政策',
        '月下追韩信，举荐人才',
        '为汉初政治制度奠定基础'
      ],
      legacy: '"成也萧何，败也萧何"，萧何是汉初最重要的政治家之一。',
      quotes: [
        '萧规曹随'
      ]
    },
    tags: ['汉初三杰', '开国功臣']
  },
  {
    id: 'hanxin',
    name: '信',
    surname: '韩',
    fullName: '韩信',
    styleName: '',
    birthYear: -231,
    deathYear: -196,
    school: 'military',
    era: '西汉',
    politics: 40,
    military: 100,
    intelligence: 85,
    charisma: 70,
    biography: {
      summary: '汉初三杰之一，被誉为"兵仙"、"战神"',
      earlyLife: '淮阴人，早年贫困，曾受胯下之辱。',
      majorEvents: [
        '投奔刘邦，经萧何举荐为大将',
        '明修栈道，暗度陈仓',
        '背水一战，破赵军',
        '垓下之战，十面埋伏灭项羽'
      ],
      achievements: [
        '率军出陈仓，定三秦',
        '破魏、代、赵、燕、齐',
        '垓下之战击败项羽',
        '被尊为"兵仙"、"战神"'
      ],
      legacy: '韩信是中国历史上最杰出的军事家之一，其用兵如神，百战百胜。',
      quotes: [
        '韩信点兵，多多益善'
      ]
    },
    tags: ['汉初三杰', '兵仙', '战神']
  },
  {
    id: 'zhangliang',
    name: '良',
    surname: '张',
    fullName: '张良',
    styleName: '子房',
    birthYear: -250,
    deathYear: -186,
    school: 'taoist',
    era: '西汉',
    politics: 80,
    military: 60,
    intelligence: 98,
    charisma: 82,
    biography: {
      summary: '汉初三杰之一，被誉为"谋圣"',
      earlyLife: '韩国人，出身贵族，秦灭韩后，曾刺杀秦始皇未遂。',
      majorEvents: [
        '博浪沙刺秦，误中副车',
        '圯上受书，得黄石公传授',
        '投奔刘邦，运筹帷幄',
        '鸿门宴上助刘邦脱险'
      ],
      achievements: [
        '运筹帷幄之中，决胜千里之外',
        '助刘邦建立汉朝',
        '功成身退，学辟谷导引',
        '被封留侯，得以善终'
      ],
      legacy: '张良是谋士的典范，功成不居，明哲保身，被尊为"谋圣"。',
      quotes: [
        '运筹帷幄之中，决胜千里之外'
      ]
    },
    tags: ['汉初三杰', '谋圣']
  },
  
  // ===== 三国时期 =====
  {
    id: 'zhugeliang',
    name: '亮',
    surname: '诸葛',
    fullName: '诸葛亮',
    styleName: '孔明',
    birthYear: 181,
    deathYear: 234,
    school: 'confucian',
    era: '三国',
    politics: 95,
    military: 85,
    intelligence: 98,
    charisma: 95,
    biography: {
      summary: '蜀汉丞相，被誉为"卧龙"、"忠臣与智者的化身"',
      earlyLife: '琅琊阳都人，早年隐居隆中，躬耕陇亩，自比管仲乐毅。',
      majorEvents: [
        '刘备三顾茅庐，隆中对策',
        '联吴抗曹，赤壁之战',
        '辅佐刘备建立蜀汉',
        '白帝城托孤，辅佐刘禅',
        '六出祁山，北伐中原'
      ],
      achievements: [
        '提出"隆中对"，规划三分天下',
        '赤壁之战联吴破曹',
        '治理蜀汉，路不拾遗',
        '发明木牛流马、诸葛连弩',
        '作《出师表》，千古传诵'
      ],
      legacy: '诸葛亮是忠臣与智者的完美化身，被后世尊为"武侯"，历代景仰。',
      quotes: [
        '鞠躬尽瘁，死而后已',
        '非淡泊无以明志，非宁静无以致远',
        '亲贤臣，远小人，此先汉所以兴隆也'
      ]
    },
    tags: ['卧龙', '武侯', '忠臣']
  },
  {
    id: 'guanyu',
    name: '羽',
    surname: '关',
    fullName: '关羽',
    styleName: '云长',
    birthYear: 160,
    deathYear: 220,
    school: 'military',
    era: '三国',
    politics: 50,
    military: 95,
    intelligence: 70,
    charisma: 92,
    biography: {
      summary: '蜀汉五虎上将之首，被尊为"武圣"',
      earlyLife: '河东解人，早年亡命奔涿郡，与刘备、张飞结为兄弟。',
      majorEvents: [
        '桃园三结义',
        '温酒斩华雄',
        '过五关斩六将',
        '单刀赴会',
        '水淹七军，威震华夏',
        '败走麦城，身死临沮'
      ],
      achievements: [
        '斩颜良诛文丑，解白马之围',
        '镇守荆州多年',
        '水淹七军，擒于禁斩庞德',
        '被尊为"武圣"，与孔子并列'
      ],
      legacy: '关羽被后世尊为"武圣"，其忠义形象深入人心，成为华人社会的道德楷模。',
      quotes: [
        '玉可碎而不可改其白，竹可焚而不可毁其节'
      ]
    },
    tags: ['武圣', '五虎上将', '美髯公']
  },
  {
    id: 'caocao',
    name: '操',
    surname: '曹',
    fullName: '曹操',
    styleName: '孟德',
    birthYear: 155,
    deathYear: 220,
    school: 'legalist',
    era: '三国',
    politics: 90,
    military: 90,
    intelligence: 92,
    charisma: 85,
    biography: {
      summary: '魏武帝，杰出的政治家、军事家、文学家',
      earlyLife: '沛国谯人，出身宦官家庭，少机警，有权数。',
      majorEvents: [
        '讨伐董卓，起兵陈留',
        '挟天子以令诸侯',
        '官渡之战，以少胜多破袁绍',
        '赤壁之战，败于孙刘联军',
        '统一北方，封魏王'
      ],
      achievements: [
        '统一中国北方',
        '实行屯田制，恢复经济',
        '唯才是举，打破门第',
        '开创建安文学，诗风慷慨悲凉',
        '精通兵法，著《孟德新书》'
      ],
      legacy: '曹操是杰出的政治家、军事家、文学家，"治世之能臣，乱世之奸雄"。',
      quotes: [
        '宁教我负天下人，休教天下人负我',
        '老骥伏枥，志在千里',
        '对酒当歌，人生几何'
      ]
    },
    tags: ['魏武帝', '奸雄']
  },
  
  // ===== 唐朝 =====
  {
    id: 'weizheng',
    name: '徵',
    surname: '魏',
    fullName: '魏徵',
    styleName: '玄成',
    birthYear: 580,
    deathYear: 643,
    school: 'confucian',
    era: '唐朝',
    politics: 95,
    military: 20,
    intelligence: 90,
    charisma: 75,
    biography: {
      summary: '唐初名相，以直言敢谏著称',
      earlyLife: '钜鹿人，早年参加瓦岗军，后降唐。',
      majorEvents: [
        '玄武门之变后归顺李世民',
        '任谏议大夫，直言进谏',
        '辅佐唐太宗开创贞观之治',
        '病逝，太宗痛哭失声'
      ],
      achievements: [
        '进谏二百余事，多被采纳',
        '主编《隋书》',
        '提出"兼听则明，偏信则暗"',
        '辅佐贞观之治'
      ],
      legacy: '魏徵是谏臣的典范，太宗称其"以人为镜，可以明得失"。',
      quotes: [
        '兼听则明，偏信则暗',
        '居安思危，戒奢以俭'
      ]
    },
    tags: ['唐初名相', '谏臣']
  },
  {
    id: 'lishimin',
    name: '世民',
    surname: '李',
    fullName: '李世民',
    styleName: '',
    birthYear: 598,
    deathYear: 649,
    school: 'confucian',
    era: '唐朝',
    politics: 95,
    military: 95,
    intelligence: 90,
    charisma: 95,
    biography: {
      summary: '唐太宗，开创贞观之治，被誉为"天可汗"',
      earlyLife: '李渊次子，少年从军，助父起兵反隋。',
      majorEvents: [
        '晋阳起兵，助父建唐',
        '平定薛仁杲、刘武周、王世充、窦建德',
        '玄武门之变，登基为帝',
        '开创贞观之治',
        '被尊为"天可汗"'
      ],
      achievements: [
        '开创贞观之治，国泰民安',
        '完善科举制度',
        '虚心纳谏，任贤使能',
        '击败突厥，扩大疆域',
        '著《帝范》教诫太子'
      ],
      legacy: '唐太宗是中国历史上最伟大的帝王之一，贞观之治成为后世楷模。',
      quotes: [
        '以铜为镜，可以正衣冠；以古为镜，可以知兴替；以人为镜，可以明得失'
      ]
    },
    tags: ['唐太宗', '天可汗']
  },
  
  // ===== 宋朝 =====
  {
    id: 'baozheng',
    name: '拯',
    surname: '包',
    fullName: '包拯',
    styleName: '希仁',
    birthYear: 999,
    deathYear: 1062,
    school: 'legalist',
    era: '北宋',
    politics: 90,
    military: 30,
    intelligence: 85,
    charisma: 88,
    biography: {
      summary: '北宋名臣，以清廉公正著称，被誉为"包青天"',
      earlyLife: '庐州合肥人，天圣五年进士，历任多地知县、知府。',
      majorEvents: [
        '任天长知县，断牛舌案闻名',
        '任开封府尹，铁面无私',
        '弹劾权贵，不畏强权',
        '病逝，朝野悲痛'
      ],
      achievements: [
        '断案如神，公正廉明',
        '弹劾张尧佐等权贵',
        '整顿吏治，严惩贪腐',
        '被民间尊为"包青天"'
      ],
      legacy: '包拯是清官的典范，其形象在民间广为流传，成为正义的象征。',
      quotes: [
        '廉者，民之表也；贪者，民之贼也'
      ]
    },
    tags: ['包青天', '清官']
  },
  {
    id: 'yuefei',
    name: '飞',
    surname: '岳',
    fullName: '岳飞',
    styleName: '鹏举',
    birthYear: 1103,
    deathYear: 1142,
    school: 'military',
    era: '南宋',
    politics: 50,
    military: 98,
    intelligence: 80,
    charisma: 95,
    biography: {
      summary: '南宋抗金名将，民族英雄，被誉为"武穆"',
      earlyLife: '相州汤阴人，少负气节，家贫力学，尤好《左氏春秋》《孙子兵法》。',
      majorEvents: [
        '应募从军，屡破金军',
        '收复建康，升任节度使',
        '郾城大捷，大败金军',
        '朱仙镇大捷，直捣黄龙在望',
        '被十二道金牌召回，冤死风波亭'
      ],
      achievements: [
        '创建岳家军，纪律严明',
        '收复襄阳六郡',
        '郾城、朱仙镇大捷',
        '作《满江红》，千古传诵',
        '被追封为鄂王'
      ],
      legacy: '岳飞是民族英雄的象征，其"精忠报国"精神激励后人，冤案令人扼腕。',
      quotes: [
        '三十功名尘与土，八千里路云和月',
        '莫等闲，白了少年头，空悲切',
        '文臣不爱钱，武臣不惜死，天下太平矣'
      ]
    },
    tags: ['武穆', '民族英雄', '精忠报国']
  },
  
  // ===== 明朝 =====
  {
    id: 'liuyong',
    name: '墉',
    surname: '刘',
    fullName: '刘墉',
    styleName: '崇如',
    birthYear: 1719,
    deathYear: 1804,
    school: 'confucian',
    era: '清朝',
    politics: 88,
    military: 20,
    intelligence: 85,
    charisma: 80,
    biography: {
      summary: '清朝名臣，书法家，被誉为"浓墨宰相"',
      earlyLife: '山东诸城人，乾隆十六年进士，历任翰林院编修、侍讲等职。',
      majorEvents: [
        '乾隆十六年进士及第',
        '历任安徽、江苏学政',
        '任体仁阁大学士',
        '与和珅周旋，保全自身'
      ],
      achievements: [
        '为官清廉，体恤民情',
        '书法造诣极高，自成一家',
        '与翁方纲、王文治、梁同书并称"清四大书家"',
        '著有《石庵诗集》'
      ],
      legacy: '刘墉以书法闻名，其政治智慧和清廉形象也深受后人敬仰。',
      quotes: []
    },
    tags: ['浓墨宰相', '书法家']
  },
  {
    id: 'zhengxie',
    name: '燮',
    surname: '郑',
    fullName: '郑燮',
    styleName: '板桥',
    birthYear: 1693,
    deathYear: 1765,
    school: 'confucian',
    era: '清朝',
    politics: 70,
    military: 20,
    intelligence: 88,
    charisma: 85,
    biography: {
      summary: '清代书画家、文学家，"扬州八怪"之首',
      earlyLife: '江苏兴化人，乾隆元年进士，曾任山东范县、潍县知县。',
      majorEvents: [
        '康熙秀才，雍正举人，乾隆进士',
        '任山东范县、潍县知县',
        '开仓赈灾，得罪上司被罢官',
        '卖画扬州，成为"扬州八怪"之首'
      ],
      achievements: [
        '诗书画三绝，尤擅画竹',
        '创"六分半书"，书法独特',
        '关心民间疾苦，开仓赈灾',
        '著《板桥全集》'
      ],
      legacy: '郑板桥是清代最著名的书画家之一，其"难得糊涂"成为千古名言。',
      quotes: [
        '难得糊涂',
        '衙斋卧听萧萧竹，疑是民间疾苦声',
        '些小吾曹州县吏，一枝一叶总关情'
      ]
    },
    tags: ['扬州八怪', '书画家']
  }
];

// 根据年份获取可用的历史人物
export function getAvailableHistoricalTalents(year: number): HistoricalTalent[] {
  return HISTORICAL_TALENTS.filter(talent => {
    // 人物在出生前10年到去世后30年内可能出现
    const appearYear = talent.birthYear - 10;
    const disappearYear = talent.deathYear + 30;
    return year >= appearYear && year <= disappearYear;
  });
}

// 根据年份和概率获取随机历史人物
export function getRandomHistoricalTalent(year: number, probability: number = 0.3): HistoricalTalent | null {
  if (Math.random() > probability) return null;
  
  const available = getAvailableHistoricalTalents(year);
  if (available.length === 0) return null;
  
  // 优先选择品质高的人物，但降低概率
  const weights = available.map(t => {
    const totalStats = t.politics + t.military + t.intelligence + t.charisma;
    // 属性越高，权重越低（稀有）
    return 400 - totalStats;
  });
  
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  let random = Math.random() * totalWeight;
  
  for (let i = 0; i < available.length; i++) {
    random -= weights[i];
    if (random <= 0) {
      return available[i];
    }
  }
  
  return available[0];
}

// 将历史人物转换为游戏人才格式
export function convertToGameTalent(historical: HistoricalTalent): Talent {
  const totalStats = historical.politics + historical.military + historical.intelligence + historical.charisma;
  let quality: 'legendary' | 'epic' | 'rare' | 'common';
  
  if (totalStats >= 350) quality = 'legendary';
  else if (totalStats >= 300) quality = 'epic';
  else if (totalStats >= 250) quality = 'rare';
  else quality = 'common';
  
  return {
    id: `historical_${historical.id}_${Date.now()}`,
    name: historical.name,
    surname: historical.surname,
    fullName: historical.fullName,
    styleName: historical.styleName,
    quality,
    school: historical.school,
    era: historical.era,
    politics: historical.politics,
    military: historical.military,
    intelligence: historical.intelligence,
    charisma: historical.charisma,
    level: 1,
    experience: 0,
    loyalty: 80, // 历史人物初始忠诚度较高
    position: null,
    description: historical.biography.summary,
    isTraining: false,
    isHistorical: true,
    historicalId: historical.id,
  };
}
